<template>
  <div>
    <v-overlay
      :value="showParticipantHorseHistory"
      opacity="0.5"
      z-index="10000"
    >
      <v-container>
        <v-card
          light
          width="1000"
        >
          <v-card-title class="third d-flex justify-space-between white--text">
            <span>Participant 10 derniers chevaux <strong v-if="selectedParticipant">{{ selectedParticipant.fullname }}</strong></span>
            <v-btn icon color="white" @click="closeOverlay">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <div class="full-width pa-4">
            <v-row no-gutters>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                color="third"
                hide-details
                class="pb-4 pt-0 mt-0 mr-4"
              />
            </v-row>
            <v-row no-gutters>
              <v-data-table
                v-model="selectedContentType"
                height="480"
                show-select
                :items="tableItems"
                item-key="id"
                :search="search"
                :loading="loading"
                :hide-default-footer="true"
                :items-per-page="-1"
                :headers="headers"
                :single-select="true"
                :custom-filter="filter"
                @item-selected="selectItem"
                @click:row="rowClick"
              >
                <template #progress>
                  <v-progress-linear
                    indeterminate
                    color="third"
                  />
                </template>
              </v-data-table>
            </v-row>
          </div>
        </v-card>
      </v-container>
    </v-overlay>
  </div>
</template>

<script>
import { ref, computed, inject, onBeforeMount } from '@vue/composition-api';

import { endOfDay, format, fromUnixTime, getUnixTime } from 'date-fns';
import { computeEffort, availableHorses } from '@/helpers/dataManager';
import { minutesToHours, sortArray } from '@/helpers/utils';
import { updateSessionAmplify } from '@/helpers/amplify';
import { getCustomerHorseHistory } from '@/helpers/request';

import store from '@/store';

export default {
  setup () {
    const showParticipantHorseHistory = inject('showParticipantHorseHistory');
    const disableAll = inject('disableAll');
    const selectedParticipant = inject('selectedParticipant');
    const selectedSession = inject('selectedSession');

    const search = ref(null);
    const selectedContentType = ref([]);
    const amountToShow = ref(10);

    const tableItems = ref([]);
    const loading = ref(false);

    const horses = computed(() => store.state.horses);

    const headers = computed(() => [
      { text: 'Session', value: 'sessionName', align: 'center', sortable: false, width: '45%' },
      { text: 'Date', value: 'date', align: 'center', sortable: false, width: '15%' },
      { text: 'Heure de début', value: 'startTime', align: 'center', sortable: false, width: '15%' },
      { text: 'Cheval', value: 'horseName', align: 'center', sortable: false, width: '35%' }
    ]);

    const contentTypeWeeklyEffort = computed(() => item => {
      const weeklySessions = store.getters.weeklySessionsByContentType(item);
      const dailySessions = store.getters.dailySessionsByContentType(item);
      return {
        dailyEffort: minutesToHours(computeEffort(dailySessions, item)),
        weeklyEffort: minutesToHours(computeEffort(weeklySessions, item))
      };
    });

    const getAvailableHorses = computed(() => availableHorses(selectedSession.value, true, true));

    const closeOverlay = () => {
      showParticipantHorseHistory.value = false;
      disableAll.value = true;
    };

    const filter = (_, searchValue, item) => item.sessionName.toLowerCase().includes(searchValue.toLowerCase()) || item.horseName.toLowerCase().includes(searchValue.toLowerCase());

    const addHorse = async selectedItem => {
      // value meaning if isSelect
      const { item, value } = selectedItem;
      const session = selectedSession.value;

      const sessionParticipants = session.participants;
      const sessionParticipantIndex = sessionParticipants.findIndex(participant => participant.uuid === selectedParticipant.value.uuid);

      if (sessionParticipantIndex !== -1) {
        // if horse already on session just add/remove from participant
        const sessionHorseIndex = session.horses.findIndex(horse => horse.uuid === item.horse);
        const participantWithHorseIndex = sessionParticipants.findIndex(participant => participant.horse === item.horse);

        if (!value) {
          if (sessionHorseIndex !== -1) {
            session.horses.splice(sessionHorseIndex, 1);

            if (participantWithHorseIndex !== -1) {
              sessionParticipants[participantWithHorseIndex].horse = null;
            }
          }
        } else {
          // horse is Selected
          if (sessionHorseIndex === -1) {
            session.horses.push({ wtitle: item.horseName, uuid: item.horse });
          }
          sessionParticipants[sessionParticipantIndex].horse = item.horse;
        }
      }

      session.participants = sessionParticipants;

      await updateSessionAmplify(session);
      showParticipantHorseHistory.value = false;
      disableAll.value = true;
    };

    const selectItem = async selectedItem => {
      const { item, value } = selectedItem;

      selectedContentType.value = value ? [item] : [];
      await addHorse(selectedItem);
    };

    const rowClick = (item, row) => {
      event.stopPropagation();
      row.select(!row.isSelected);
    };

    onBeforeMount(async () => {
      loading.value = true;
      if (!store.state.isLazyLoaded.horses) {
        await store.dispatch('getHorses');
      }

      // get customer last sessions
      if (selectedParticipant.value.uuid && selectedSession.value) {
        const response = await getCustomerHorseHistory({ customerUuid: selectedParticipant.value.uuid, date: getUnixTime(endOfDay(fromUnixTime(selectedSession.value.end_time))), size: 50 });

        const sessions = response.success ? sortArray(response.data, 'end_time', '>') : [];

        const sessionsWithParticipantAndHorse = sessions.filter(session => (session.participants || []).some(p => p.uuid === selectedParticipant.value.uuid && p.horse));

        const sessionsToShow = sessionsWithParticipantAndHorse.slice(0, amountToShow.value);

        sessionsToShow.map(session => {
          const participant = (session.participants || []).find(p => p.uuid === selectedParticipant.value.uuid);
          const horse = horses.value.find(h => h.uuid === participant.horse);

          if (horse) {
            // verify if horse is available
            const isHorseAvailable = getAvailableHorses.value.some(h => h.uuid === participant.horse);

            const horseEffort = { dailyEffort: contentTypeWeeklyEffort.value(horse).dailyEffort, weeklyEffort: contentTypeWeeklyEffort.value(horse).weeklyEffort };

            if (isHorseAvailable) {
              tableItems.value.push({
                id: `${session.id}_${participant.horse}`,
                sessionName: session.name,
                date: format(fromUnixTime(session.start_time), 'dd-MM-yyyy'),
                startTime: format(fromUnixTime(session.start_time), 'HH:mm'),
                endTime: format(fromUnixTime(session.end_time), 'HH:mm'),
                horse: participant.horse,
                horseName: horse ? horse.horse_name : '',
                dailyEffort: horseEffort.dailyEffort,
                weeklyEffort: horseEffort.weeklyEffort
              });
            }
          }
        });
      }
      loading.value = false;
    });

    return { loading, showParticipantHorseHistory, disableAll, closeOverlay, horses, search, headers, selectedContentType, selectItem, rowClick, contentTypeWeeklyEffort, selectedParticipant, filter, tableItems };
  }
};
</script>
